// src/pages/Admin/AdminRiders.tsx
import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../api';
import { motion } from 'framer-motion';
import { Search, Bike, Star, MapPin, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface Rider {
  _id: string;
  fullName: string;
  email: string;
  phone?: string;
  profileImage?: string;
  bikeInfo?: {
    bikeImage?: string;
    bikeModel?: string;
    licensePlate?: string;
    color?: string;
  };
  rating: number;
  completedOrders: number;
  totalEarnings: number;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
}

const AdminRiders: React.FC = () => {
  const [riders, setRiders] = useState<Rider[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  useEffect(() => {
    fetchRiders();
  }, [page, search]);

  const fetchRiders = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getUserManagement({
        page,
        limit,
        role: 'rider',
        search: search || undefined,
      });
      if (res.data.success) {
        setRiders(res.data.data.users);
        setTotalPages(res.data.data.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching riders:', error);
      toast.error('Failed to load riders');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId: string, current: boolean) => {
    try {
      const res = await adminAPI.updateUserStatus(userId, { isActive: !current });
      if (res.data.success) {
        toast.success(`Rider ${!current ? 'activated' : 'deactivated'}`);
        fetchRiders();
      }
    } catch (error) {
      toast.error('Failed to update rider');
    }
  };

  const handleToggleVerified = async (userId: string, current: boolean) => {
    try {
      const res = await adminAPI.updateUserStatus(userId, { isVerified: !current });
      if (res.data.success) {
        toast.success(`Rider ${!current ? 'verified' : 'unverified'}`);
        fetchRiders();
      }
    } catch (error) {
      toast.error('Failed to update verification');
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Rider Management</h1>

      <div className="bg-white rounded-2xl p-4 shadow-lg mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search riders by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {riders.map((rider) => (
              <motion.div
                key={rider._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-2xl p-4 shadow-lg border"
              >
                <div className="flex items-start gap-3">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                    {rider.profileImage ? (
                      <img src={rider.profileImage} alt={rider.fullName} className="h-full w-full object-cover" />
                    ) : (
                      rider.fullName.charAt(0)
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">{rider.fullName}</h3>
                    <p className="text-sm text-gray-600">{rider.email}</p>
                    {rider.phone && <p className="text-xs text-gray-500">{rider.phone}</p>}
                    <div className="flex items-center gap-3 mt-2 text-sm">
                      <span className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                        {rider.rating.toFixed(1)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Bike className="h-4 w-4" />
                        {rider.completedOrders} rides
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bike info */}
                {rider.bikeInfo && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                    <p><strong>Model:</strong> {rider.bikeInfo.bikeModel || 'N/A'}</p>
                    <p><strong>Plate:</strong> {rider.bikeInfo.licensePlate || 'N/A'}</p>
                    <p><strong>Color:</strong> {rider.bikeInfo.color || 'N/A'}</p>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleActive(rider._id, rider.isActive)}
                      className={`px-3 py-1 rounded-lg text-sm flex items-center gap-1 ${
                        rider.isActive ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {rider.isActive ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                      {rider.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleToggleVerified(rider._id, rider.isVerified)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        rider.isVerified ? 'bg-gray-100 text-gray-700' : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {rider.isVerified ? 'Verified' : 'Verify'}
                    </button>
                  </div>
                  <span className="text-sm font-bold text-emerald-600">
                    ₦{(rider.totalEarnings || 0).toLocaleString()}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-6 gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminRiders;